import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { useGuestMode } from '../hooks/useGuestMode';
import { useRoutes } from '../hooks/useRoutes';
import { AppHeader } from '../components/AppHeader';
import { LandingView } from '../components/LandingView';
import { RivalCard } from '../components/RivalCard';
import { RankChangeBanner } from '../components/RankChangeBanner';
import { OnboardingModal } from '../components/OnboardingModal';
import { LastSeasonBanner } from '../components/LastSeasonBanner';
import { StreakBanner } from '../components/StreakBanner';
import { FloatingHub } from '../components/FloatingHub';
import { AdBanner, AD_SLOTS } from '../components/AdBanner';
import { formatTime } from '@sudoku/shared';
import { PlayMenuSheet, type PlayMenuItem } from '../components/PlayMenuSheet';
import { DifficultySheet } from '../components/DifficultySheet';
import { RankedMatchModal } from '../components/RankedMatchModal';
import './HomePage.css';
import '../components/LandingView.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ActiveGame {
  game_id: string;
  difficulty: number;
  elapsed_time: number;
  mistakes: number;
}

const IconSolo = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
    <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
  </svg>
);
const IconQuick = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h7l-1 8 10-12h-7z"/>
  </svg>
);
const IconFriends = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="3"/><circle cx="16" cy="8" r="3"/>
    <path d="M2 20c0-3.3 2.7-6 6-6"/><path d="M16 14c3.3 0 6 2.7 6 6"/>
  </svg>
);
const IconDaily = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconRanked = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z"/>
    <path d="M5 4H3v3a3 3 0 0 0 4 3"/><path d="M19 4h2v3a3 3 0 0 1-4 3"/>
  </svg>
);
const IconChallenge = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
  </svg>
);
const IconPremium = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 5 5.6.8-4 3.9.9 5.6L12 14.8 7.1 17.3 8 11.7 4 7.8 9.6 7z"/>
  </svg>
);
const IconBattlePass = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3h14l-2 7 2 11-9-4-9 4 2-11z"/>
  </svg>
);
const IconWallet = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="18" height="14" rx="2"/>
    <path d="M3 10h18M16 14h2"/>
  </svg>
);

export function HomePage() {
  const navigate = useNavigate();
  const { nav, r } = useRoutes();
  const { t } = useI18n();
  const { token, user } = useAuth();
  const { isGuest, enterGuest } = useGuestMode();
  const [activeGame, setActiveGame] = useState<ActiveGame | null>(null);
  const [dailyPending, setDailyPending] = useState(false);
  const [funOpen, setFunOpen] = useState(false);
  const [rankedOpen, setRankedOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [rankedMatchOpen, setRankedMatchOpen] = useState(false);
  const [diffSheet, setDiffSheet] = useState<null | 'solo' | 'ranked'>(null);
  const [searchParams] = useSearchParams();

  // /oyna gibi zorluk parametresi olmadan ziyaret edilen bir sayfadan buraya
  // yönlendirilmiş olabiliriz (bkz. GamePage.tsx) — DifficultySheet'i otomatik
  // aç ve URL'i temizle, tek tutarlı zorluk seçme deneyimi için.
  useEffect(() => {
    const mode = searchParams.get('openDifficulty');
    if (mode === 'solo' || mode === 'ranked') {
      setDiffSheet(mode);
      navigate(r('home'), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showRankedLock, setShowRankedLock] = useState(false);
  const [myRating, setMyRating] = useState(1000);
  const [playerId] = useState(() => {
    if (user?.id) return user.id;
    let id = localStorage.getItem('sudoku_player_id');
    if (!id) { id = 'p_' + Math.random().toString(36).slice(2, 10); localStorage.setItem('sudoku_player_id', id); }
    return id;
  });

  // Ziyaretçi (anonymous + guest mode kapalı) → Landing
  const isVisitor = !user && !isGuest;
  // Misafir (anonymous + guest mode açık) → Dashboard ama ranked kilitli
  const isGuestUser = !user && isGuest;

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/stats/season`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const sr = d?.my_rating?.rating;
        if (typeof sr === 'number') { setMyRating(sr); return; }
        return fetch(`${API}/stats/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(d2 => { if (d2?.rating) setMyRating(d2.rating); });
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) { setActiveGame(null); return; }
    let aborted = false;
    fetch(`${API}/game/resume`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (aborted || !data?.active) return;
        setActiveGame({
          game_id: data.active.game_id,
          difficulty: data.active.difficulty,
          elapsed_time: data.active.elapsed_time,
          mistakes: data.active.mistakes,
        });
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token]);

  // Daily kırmızı nokta — bugün hiç çözülmemiş zorluk varsa göster
  useEffect(() => {
    if (!token) { setDailyPending(false); return; }
    let aborted = false;
    fetch(`${API}/daily/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (aborted || !data?.puzzles) return;
        const anyPending = (data.puzzles as Array<{ status: string }>).some(p =>
          p.status === 'not_started' || p.status === 'in_progress'
        );
        setDailyPending(anyPending);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token]);

  const funItems: PlayMenuItem[] = [
    { key: 'solo',   title: t.home.funMenuSolo,   desc: t.home.funMenuSoloDesc,   icon: IconSolo,    accent: '#A8C2A6', onClick: () => setDiffSheet('solo') },
    { key: 'quick',  title: t.home.funMenuQuick,  desc: t.home.funMenuQuickDesc,  icon: IconQuick,   accent: '#9CB4D1', onClick: () => nav('lobby') },
    { key: 'daily',  title: t.home.funMenuDaily,  desc: t.home.funMenuDailyDesc,  icon: IconDaily,   accent: '#7C6FCB', onClick: () => nav('daily'), badge: dailyPending ? 'dot' : null },
  ];
  const rankedItems: PlayMenuItem[] = [
    { key: 'ranked-solo',  title: (t.home as any).rankedMenuSolo,   desc: (t.home as any).rankedMenuSoloDesc,   icon: IconRanked,    accent: '#B68A4E', onClick: () => isGuestUser ? setShowRankedLock(true) : setDiffSheet('ranked') },
    { key: 'ranked-match', title: (t.home as any).rankedMenuMatch,  desc: (t.home as any).rankedMenuMatchDesc,  icon: IconChallenge, accent: '#A06FCB', onClick: () => isGuestUser ? setShowRankedLock(true) : (user ? setRankedMatchOpen(true) : nav('auth')) },
    { key: 'friend-play',  title: (t.home as any).rankedMenuFriend, desc: (t.home as any).rankedMenuFriendDesc, icon: IconFriends,   accent: '#C9A66B', onClick: () => isGuestUser ? setShowRankedLock(true) : (user ? nav('challenge') : nav('auth')) },
  ];
  const sh = (t as any).shopHub ?? {};
  const shopItems: PlayMenuItem[] = [
    { key: 'premium',    title: sh.premium,    desc: sh.premiumDesc,    icon: IconPremium,    accent: '#C9A66B', onClick: () => navigate(r('premium')) },
    { key: 'battlepass', title: sh.battlepass, desc: sh.battlepassDesc, icon: IconBattlePass, accent: '#D9C28F', onClick: () => navigate('/battlepass') },
    { key: 'wallet',     title: sh.wallet,     desc: sh.walletDesc,     icon: IconWallet,     accent: '#A8C2A6', onClick: () => navigate('/wallet') },
  ];

  // ─── 1. Ziyaretçi (visitor) — Landing + SEO içerik ──
  if (isVisitor) {
    return <LandingView onTryAsGuest={enterGuest} />;
  }

  // ─── 2 & 3. Misafir (guest) ve Kayıtlı kullanıcı — temiz Dashboard ──
  return (
    <div className="home-page">
      <SEO path="/" />
      <AppHeader />

      <OnboardingModal />

      {/* Banner satırı: web'de yan yana, mobilde yatay scroll */}
      <div className="home-banner-row">
        <RivalCard />
        <RankChangeBanner />
        <LastSeasonBanner />
        <StreakBanner />
      </div>

      <FloatingHub />

      {/* Aktif oyun varsa — devam et */}
      {activeGame && (
        <div
          className="home-resume-banner"
          onClick={() => navigate(`${r('play')}?resume=${activeGame.game_id}`)}
        >
          <div className="home-resume-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          </div>
          <div className="home-resume-text">
            <strong>{t.home.resumeTitle}</strong>
            <span>D{activeGame.difficulty} · {formatTime(activeGame.elapsed_time)} · {activeGame.mistakes}/3</span>
          </div>
          <span className="home-resume-arr">→</span>
        </div>
      )}

      {/* 2 BÜYÜK kart: Eğlence + Dereceli */}
      <div className="home-hero-cards">
        <button type="button" className="home-hero-card home-hero-card--fun" onClick={() => setFunOpen(true)}>
          {dailyPending && <span className="home-hero-dot" aria-hidden="true" />}
          <div className="home-hero-card-bg" aria-hidden="true">
            <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="24" y="24" width="152" height="152" rx="18"/>
              <line x1="75" y1="24" x2="75" y2="176"/>
              <line x1="125" y1="24" x2="125" y2="176"/>
              <line x1="24" y1="75" x2="176" y2="75"/>
              <line x1="24" y1="125" x2="176" y2="125"/>
              <circle cx="50" cy="50" r="3" fill="currentColor" stroke="none"/>
              <circle cx="150" cy="50" r="3" fill="currentColor" stroke="none"/>
              <circle cx="100" cy="100" r="3" fill="currentColor" stroke="none"/>
              <circle cx="50" cy="150" r="3" fill="currentColor" stroke="none"/>
              <circle cx="150" cy="150" r="3" fill="currentColor" stroke="none"/>
            </svg>
          </div>
          <div className="home-hero-content">
            <span className="home-hero-eyebrow">{t.home.funCardCta}</span>
            <span className="home-hero-title">{t.home.funCardTitle}</span>
            <span className="home-hero-sub">{t.home.funCardSubtitle}</span>
            <span className="home-hero-cta">{t.home.funCardCta} →</span>
          </div>
        </button>

        <button type="button" className="home-hero-card home-hero-card--ranked" onClick={() => isGuestUser ? setShowRankedLock(true) : setRankedOpen(true)}>
          <div className="home-hero-card-bg" aria-hidden="true">
            <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M70 168h60"/>
              <path d="M100 138v30"/>
              <path d="M60 30h80v44a40 40 0 0 1-80 0z"/>
              <path d="M60 38H38v22a22 22 0 0 0 22 22"/>
              <path d="M140 38h22v22a22 22 0 0 1-22 22"/>
              <circle cx="100" cy="58" r="6"/>
              <path d="M93 78l7-12 7 12-7 4z"/>
            </svg>
          </div>
          <div className="home-hero-content">
            <span className="home-hero-eyebrow">{t.home.rankedCardCta}</span>
            <span className="home-hero-title">{t.home.rankedCardTitle}</span>
            <span className="home-hero-sub">{t.home.rankedCardSubtitle}</span>
            <span className="home-hero-cta">{t.home.rankedCardCta} →</span>
          </div>
        </button>
      </div>

      {/* Araçlar & Eğitim — yan yana iki kart + tutorial alt kart */}
      <div className="home-tools-section">
        <h2 className="home-tools-title">{t.home.toolsSectionTitle}</h2>
        <div className="home-tools-grid">
          <button type="button" className="home-tool-card home-tool-card--solver" onClick={() => nav('solver')}>
            <div className="home-tool-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
                <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
                <path d="M6 6l3 3-3 3"/>
              </svg>
            </div>
            <div className="home-tool-text">
              <strong>{t.home.solverCardTitle}</strong>
              {!!t.home.solverCardDesc && <span>{t.home.solverCardDesc}</span>}
            </div>
            <span className="home-tool-arr" aria-hidden="true">→</span>
          </button>
          <button type="button" className="home-tool-card home-tool-card--academy" onClick={() => nav('education')}>
            <div className="home-tool-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l10 5-10 5L2 8z"/>
                <path d="M6 10v5c0 1.5 3 3 6 3s6-1.5 6-3v-5"/>
                <path d="M22 8v6"/>
              </svg>
            </div>
            <div className="home-tool-text">
              <strong>{t.home.academyCardTitle}</strong>
              {!!t.home.academyCardDesc && <span>{t.home.academyCardDesc}</span>}
            </div>
            <span className="home-tool-arr" aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      {/* Mağaza hero kartı */}
      <button
        type="button"
        className="home-shop-hero-card"
        onClick={() => setShopOpen(true)}
        aria-label={t.home.shopCardTitle}
      >
        <svg className="home-shop-hero-bg" viewBox="0 0 360 120" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <defs>
            <linearGradient id="shopBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7C2D8F" />
              <stop offset="55%" stopColor="#C9341F" />
              <stop offset="100%" stopColor="#F39C12" />
            </linearGradient>
            <radialGradient id="shopGlow" cx="75%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFE680" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#FFE680" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="360" height="120" fill="url(#shopBg)" />
          <rect x="0" y="0" width="360" height="120" fill="url(#shopGlow)" />
          <circle cx="310" cy="24" r="2.5" fill="#FFE680" opacity="0.95" />
          <circle cx="335" cy="65" r="1.8" fill="#FFFFFF" opacity="0.85" />
          <circle cx="285" cy="96" r="2" fill="#FFE680" opacity="0.9" />
          <circle cx="250" cy="28" r="1.6" fill="#FFFFFF" opacity="0.8" />
          <circle cx="200" cy="14" r="1.4" fill="#FFE680" opacity="0.75" />
        </svg>

        {/* Hazine sandığı */}
        <svg className="home-shop-chest" viewBox="0 0 130 120" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="coinGr2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FFE680" />
              <stop offset="100%" stopColor="#D49A1A" />
            </linearGradient>
            <linearGradient id="chestGr2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A0521C" />
              <stop offset="100%" stopColor="#5A2E0F" />
            </linearGradient>
            <linearGradient id="chestLid2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#C97A2F" />
              <stop offset="100%" stopColor="#7A3D14" />
            </linearGradient>
          </defs>
          <rect x="28" y="65" width="74" height="42" rx="4" fill="url(#chestGr2)" stroke="#3A1D08" strokeWidth="1.5" />
          <path d="M22 38 L108 38 Q116 38 116 46 L116 65 L14 65 L14 46 Q14 38 22 38 Z" fill="url(#chestLid2)" stroke="#3A1D08" strokeWidth="1.5" transform="rotate(-18 65 50)" />
          <rect x="60" y="78" width="10" height="12" rx="2" fill="#F39C12" stroke="#5A2E0F" strokeWidth="1" />
          <circle cx="45" cy="62" r="9" fill="url(#coinGr2)" stroke="#8A5F12" strokeWidth="1.2" />
          <circle cx="62" cy="58" r="10" fill="url(#coinGr2)" stroke="#8A5F12" strokeWidth="1.2" />
          <circle cx="82" cy="62" r="9" fill="url(#coinGr2)" stroke="#8A5F12" strokeWidth="1.2" />
          <circle cx="53" cy="48" r="7" fill="url(#coinGr2)" stroke="#8A5F12" strokeWidth="1.2" />
          <circle cx="72" cy="45" r="8" fill="url(#coinGr2)" stroke="#8A5F12" strokeWidth="1.2" />
          <path d="M95 22 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill="#FFE680" />
          <path d="M30 30 l1.5 3 3 1.5 -3 1.5 -1.5 3 -1.5 -3 -3 -1.5 3 -1.5 z" fill="#FFFFFF" />
        </svg>

        {/* İçerik */}
        <div className="home-shop-content">
          <span className="home-shop-eyebrow">{t.home.shopCardEyebrow}</span>
          <span className="home-shop-title">{t.home.shopCardTitle}</span>
          <span className="home-shop-cta">{t.home.shopCardCta} →</span>
        </div>
      </button>

      {/* 150px boşluk: tıklanabilir butonlarla yanlışlıkla reklam tıklamasını önler (AdSense politikası) */}
      <div style={{ maxWidth: 900, margin: '150px auto 0', padding: '0 12px' }}>
        <AdBanner slot={AD_SLOTS.home} />
      </div>

      {/* Guest → Ranked kilit modali */}
      {showRankedLock && (
        <div className="guest-lock-backdrop" onClick={() => setShowRankedLock(false)}>
          <div className="guest-lock-card" onClick={e => e.stopPropagation()}>
            <div className="guest-lock-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h3 className="guest-lock-title">{(t.home as any).guestLockRankedTitle}</h3>
            <p className="guest-lock-desc">{(t.home as any).guestLockRankedDesc}</p>
            <div className="guest-lock-actions">
              <button
                type="button"
                className="guest-lock-btn guest-lock-btn--primary"
                onClick={() => navigate('/auth')}
              >
                {(t.home as any).guestLockRegisterNow}
              </button>
              <button
                type="button"
                className="guest-lock-btn guest-lock-btn--ghost"
                onClick={() => setShowRankedLock(false)}
              >
                {(t.home as any).guestLockKeepPlaying}
              </button>
            </div>
          </div>
        </div>
      )}

      <PlayMenuSheet
        open={funOpen}
        title={t.home.funMenuTitle}
        closeLabel={t.home.menuClose}
        items={funItems}
        variant="fun"
        onClose={() => setFunOpen(false)}
      />
      <PlayMenuSheet
        open={rankedOpen}
        title={t.home.rankedMenuTitle}
        closeLabel={t.home.menuClose}
        items={rankedItems}
        variant="ranked"
        onClose={() => setRankedOpen(false)}
      />
      <PlayMenuSheet
        open={shopOpen}
        title={sh.title}
        closeLabel={t.home.menuClose}
        items={shopItems}
        variant="shop"
        onClose={() => setShopOpen(false)}
      />
      <DifficultySheet
        open={diffSheet !== null}
        mode={diffSheet ?? 'solo'}
        closeLabel={t.home.menuClose}
        onClose={() => setDiffSheet(null)}
        onPick={(d) => {
          const isRanked = diffSheet === 'ranked';
          const params = new URLSearchParams();
          params.set('difficulty', String(d));
          if (isRanked) params.set('mode', 'ranked');
          navigate(`${r('play')}?${params.toString()}`);
        }}
      />
      <RankedMatchModal
        open={rankedMatchOpen}
        onClose={() => setRankedMatchOpen(false)}
        token={token}
        user={user}
        myRating={myRating}
        playerId={playerId}
      />
    </div>
  );
}
